import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface SubLevel {
  minValue: number;
  maxValue: number;
}

interface Level {
  level: number;
  objective: string;
  maxBars: number;
  subLevels: SubLevel[];
}

const LEVELS = levelsData.levels as Level[];
const SEGMENTS_PER_BAR = 10;

function fmtDecimal(v: number): string {
  return v.toFixed(1).replace('.', ',');
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Gera (a, b) tal que 0,1 ≤ a, b e min ≤ a+b ≤ max. Tudo em décimos (int). */
function generateOperands(min: number, max: number): { a: number; b: number; sum: number } {
  const minTenths = Math.max(2, Math.round(min * 10)); // soma >= 0,2 pra ter ao menos a=b=0,1
  const maxTenths = Math.round(max * 10);
  const sumT = randInt(minTenths, maxTenths);
  // a entre 1 e sumT-1 (B fica >= 1)
  const aT = randInt(1, sumT - 1);
  const bT = sumT - aT;
  return { a: aT, b: bT, sum: sumT };
}

export function mountLearnDecimalsAdditions(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;
  const totalSubs = level.subLevels.length;

  let subIndex = 0;
  let correctCount = 0;
  let aTenths = 0;
  let bTenths = 0;
  let targetTenths = 0;
  let bars: number[] = [];
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Some decimais</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage ldec">
      <div class="feedback" aria-live="polite"></div>
      <p class="ldec__progress" aria-live="polite"></p>
      <div class="ldec__equation" aria-live="polite"></div>
      <p class="ldec__current" aria-live="polite"></p>
      <div class="ldec__bars"></div>
      <div class="ldec__actions">
        <button class="btn btn--secondary ldec__reset" type="button">↺ Limpar</button>
        <button class="btn btn--primary ldec__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.ldec__progress')!;
  const equationEl = page.querySelector<HTMLElement>('.ldec__equation')!;
  const currentEl = page.querySelector<HTMLElement>('.ldec__current')!;
  const barsEl = page.querySelector<HTMLElement>('.ldec__bars')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.ldec__reset')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.ldec__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    bars = new Array(level.maxBars).fill(0);
    ctx.audio.play('click');
    renderAll();
  });
  confirmBtn.addEventListener('click', validate);

  function startSubLevel(): void {
    const sub = level.subLevels[subIndex] ?? level.subLevels[0]!;
    const { a, b, sum } = generateOperands(sub.minValue, sub.maxValue);
    aTenths = a;
    bTenths = b;
    targetTenths = sum;
    bars = new Array(level.maxBars).fill(0);
    buttonsBlocked = false;
    renderAll();
  }

  function currentTenths(): number {
    return bars.reduce((acc, v) => acc + v, 0);
  }

  function renderAll(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
    equationEl.innerHTML = `<strong>${fmtDecimal(aTenths / 10)}</strong> + <strong>${fmtDecimal(bTenths / 10)}</strong> = ?`;
    currentEl.textContent = `Pintado: ${fmtDecimal(currentTenths() / 10)}`;
    renderBars();
  }

  function renderBars(): void {
    barsEl.innerHTML = '';
    for (let i = 0; i < level.maxBars; i++) {
      const bar = document.createElement('div');
      bar.className = 'ldec__bar';
      for (let s = SEGMENTS_PER_BAR; s >= 1; s--) {
        const seg = document.createElement('div');
        seg.className = 'ldec__seg';
        if (s <= bars[i]!) seg.classList.add('ldec__seg--filled');
        seg.textContent = (s / 10).toFixed(1).replace('.', ',');
        seg.addEventListener('click', () => onSegmentClick(i, s));
        bar.appendChild(seg);
      }
      barsEl.appendChild(bar);
    }
  }

  function onSegmentClick(barIdx: number, segLevel: number): void {
    if (buttonsBlocked) return;
    if (bars[barIdx]! >= segLevel) bars[barIdx] = segLevel - 1;
    else bars[barIdx] = segLevel;
    ctx.audio.play('click');
    currentEl.textContent = `Pintado: ${fmtDecimal(currentTenths() / 10)}`;
    renderBars();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (buttonsBlocked) return;
    if (currentTenths() === targetTenths) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) startSubLevel();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} somas.</p>
      <p class="activity-finish__text" style="font-size:14px;color:var(--color-text-muted);">${level.objective}</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/learn_decimals_additions/${level.level + 1}`; });
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
